import type {
  DashboardCardType,
  DashboardLayout,
  DashboardLayoutItem,
  DashboardLayoutZone,
  DashboardSplitSlot,
} from "../../../../types/dashboardLayout";
import {
  DASHBOARD_CARD_TYPES,
  DASHBOARD_LAYOUT_VERSION,
  DASHBOARD_LAYOUT_ZONES,
  DASHBOARD_SPLIT_SLOTS,
} from "../../../../types/dashboardLayout";
import {
  DASHBOARD_MAX_MINI_STAT_SLOTS,
  dashboardCardRegistry,
  getDefaultDashboardLayout,
  getDefaultDashboardLayoutItems,
  type DashboardDefaultLayoutVariant,
} from "./dashboardCardRegistry";
import { coalesceSplitChartRows, isSplittableChartRowCard } from "./dashboardChartRows";

function enforceMiniStatSlotLimit(items: DashboardLayoutItem[]): DashboardLayoutItem[] {
  const visibleMini = items
    .filter((item) => item.zone === "miniStats" && item.visible)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (visibleMini.length <= DASHBOARD_MAX_MINI_STAT_SLOTS) return items;

  const keepTypes = new Set(
    visibleMini.slice(0, DASHBOARD_MAX_MINI_STAT_SLOTS).map((item) => item.cardType)
  );
  return items.map((item) =>
    item.zone === "miniStats" && item.visible && !keepTypes.has(item.cardType)
      ? { ...item, visible: false }
      : item
  );
}

const DASHBOARD_CARD_TYPE_SET = new Set<string>(DASHBOARD_CARD_TYPES);
const DASHBOARD_LAYOUT_ZONE_SET = new Set<string>(DASHBOARD_LAYOUT_ZONES);
const DASHBOARD_SPLIT_SLOT_SET = new Set<string>(DASHBOARD_SPLIT_SLOTS);

/** Legacy per-chart widgets merged into `tradeDistribution`. */
const LEGACY_TRADE_DISTRIBUTION_CARD_TYPES = new Set([
  "distributionBySymbol",
  "longVsShort",
  "winVsLoss",
]);

export function isDashboardCardType(value: string): value is DashboardCardType {
  return DASHBOARD_CARD_TYPE_SET.has(value);
}

function asNonNegativeNumber(value: unknown, fallback: number): number {
  if (typeof value !== "number" || Number.isNaN(value) || !Number.isFinite(value) || value < 0) {
    return fallback;
  }
  return value;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return fallback;
}

/** Maps API / DB rows (snake_case, numeric booleans) into the shape `normalizeLayoutItem` expects. */
function coerceRawLayoutItem(raw: unknown): Partial<DashboardLayoutItem> {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;
  const cardType = r.cardType ?? r.card_type;
  const zone = r.zone ?? r.layout_zone;
  const visible = r.visible ?? r.is_visible;
  const order = r.order ?? r.sort_order;
  const slot = r.slot ?? r.split_slot;
  const ratio = r.ratio ?? r.split_ratio;
  return {
    ...(r as object),
    ...(typeof cardType === "string" ? { cardType } : {}),
    ...(typeof zone === "string" ? { zone } : {}),
    ...(visible !== undefined && visible !== null ? { visible } : {}),
    ...(order !== undefined && order !== null ? { order: Number(order) } : {}),
    ...(typeof slot === "string" ? { slot } : {}),
    ...(ratio !== undefined && ratio !== null ? { ratio: Number(ratio) } : {}),
  } as Partial<DashboardLayoutItem>;
}

function itemHasExplicitVisibleFlag(raw: unknown): boolean {
  if (!raw || typeof raw !== "object") return false;
  const r = raw as Record<string, unknown>;
  return "visible" in r || "is_visible" in r;
}

function asZone(value: unknown, fallback: DashboardLayoutZone): DashboardLayoutZone {
  if (typeof value === "string" && DASHBOARD_LAYOUT_ZONE_SET.has(value)) {
    return value as DashboardLayoutZone;
  }
  return fallback;
}

function asSplitSlot(value: unknown, fallback?: DashboardSplitSlot): DashboardSplitSlot | undefined {
  if (typeof value === "string" && DASHBOARD_SPLIT_SLOT_SET.has(value)) {
    return value as DashboardSplitSlot;
  }
  return fallback;
}

function migrateLegacyTradeDistributionItems(source: unknown[]): unknown[] {
  let legacyVisible = false;
  let legacyOrder = dashboardCardRegistry.tradeDistribution.defaultItem.order ?? 2;
  let tradeDistributionRaw: unknown = null;

  const filtered: unknown[] = [];

  for (const raw of source) {
    const candidate = coerceRawLayoutItem(raw);
    const cardType = candidate.cardType;
    if (!cardType) {
      filtered.push(raw);
      continue;
    }

    if (LEGACY_TRADE_DISTRIBUTION_CARD_TYPES.has(cardType)) {
      const visibleDefault = itemHasExplicitVisibleFlag(raw)
        ? dashboardCardRegistry.tradeDistribution.defaultItem.visible
        : true;
      if (asBoolean(candidate.visible, visibleDefault)) {
        legacyVisible = true;
      }
      legacyOrder = Math.min(
        legacyOrder,
        asNonNegativeNumber(candidate.order, legacyOrder)
      );
      continue;
    }

    if (cardType === "tradeDistribution") {
      tradeDistributionRaw = raw;
      continue;
    }

    filtered.push(raw);
  }

  if (tradeDistributionRaw) {
    const candidate = coerceRawLayoutItem(tradeDistributionRaw);
    const visibleDefault = itemHasExplicitVisibleFlag(tradeDistributionRaw)
      ? dashboardCardRegistry.tradeDistribution.defaultItem.visible
      : true;
    const visible = asBoolean(candidate.visible, visibleDefault) || legacyVisible;
    filtered.push({
      ...(typeof tradeDistributionRaw === "object" && tradeDistributionRaw !== null
        ? tradeDistributionRaw
        : {}),
      cardType: "tradeDistribution",
      visible,
      zone: "fullWidth",
      order: Math.min(asNonNegativeNumber(candidate.order, legacyOrder), legacyOrder),
    });
  } else if (legacyVisible) {
    filtered.push({
      cardType: "tradeDistribution",
      visible: true,
      zone: "fullWidth",
      order: legacyOrder,
    });
  }

  return filtered;
}

function normalizeLayoutItemPlacement(
  candidate: Partial<DashboardLayoutItem>,
  fallback: DashboardLayoutItem
): Pick<DashboardLayoutItem, "slot" | "ratio"> {
  const slot = asSplitSlot(candidate.slot, fallback.slot);
  if (slot) {
    return {
      slot,
      ratio: asNonNegativeNumber(candidate.ratio, fallback.ratio ?? 0.5),
    };
  }
  if (candidate.ratio !== undefined && candidate.ratio !== null) {
    return { ratio: asNonNegativeNumber(candidate.ratio, fallback.ratio ?? 0.5) };
  }
  return {};
}

/** Visible layout rows sent to the dashboard layouts API (zone model v2+). */
export function serializeDashboardLayoutItemForApi(item: DashboardLayoutItem): DashboardLayoutItem {
  const payload: DashboardLayoutItem = {
    cardType: item.cardType,
    visible: item.visible,
    zone: item.zone,
    order: item.order ?? 0,
  };

  if (item.slot) {
    payload.slot = item.slot;
    payload.ratio = item.ratio ?? 0.5;
  }

  if (item.x !== undefined) payload.x = item.x;
  if (item.y !== undefined) payload.y = item.y;
  if (item.w !== undefined) payload.w = item.w;
  if (item.h !== undefined) payload.h = item.h;
  if (item.minW !== undefined) payload.minW = item.minW;
  if (item.minH !== undefined) payload.minH = item.minH;
  if (item.maxW !== undefined) payload.maxW = item.maxW;
  if (item.maxH !== undefined) payload.maxH = item.maxH;
  if (item.static !== undefined) payload.static = item.static;

  return payload;
}

export function serializeDashboardLayoutItemsForApi(items: DashboardLayoutItem[]): DashboardLayoutItem[] {
  return items.filter((item) => item.visible).map(serializeDashboardLayoutItemForApi);
}

export function normalizeLayoutItems(
  items: unknown,
  options?: { defaultLayoutVariant?: DashboardDefaultLayoutVariant }
): DashboardLayoutItem[] {
  const source = migrateLegacyTradeDistributionItems(Array.isArray(items) ? items : []);
  const dedupedCardTypes = new Set<DashboardCardType>();
  const normalized: DashboardLayoutItem[] = [];

  for (const raw of source) {
    const candidate = coerceRawLayoutItem(raw);
    const cardType = candidate.cardType;
    if (!cardType || !isDashboardCardType(cardType)) continue;
    if (dedupedCardTypes.has(cardType)) continue;

    const fallback = dashboardCardRegistry[cardType].defaultItem;

    dedupedCardTypes.add(cardType);
    // Backends often persist only “on” widgets and omit `visible`; registry defaults are mostly false,
    // so missing `visible` must mean shown — otherwise refresh wipes custom layouts back to ~defaults.
    const visibleDefault = itemHasExplicitVisibleFlag(raw) ? fallback.visible : true;
    const placement = normalizeLayoutItemPlacement(candidate, fallback);
    normalized.push({
      ...fallback,
      ...candidate,
      cardType,
      visible: asBoolean(candidate.visible, visibleDefault),
      zone: asZone(candidate.zone, fallback.zone),
      order: asNonNegativeNumber(candidate.order, fallback.order ?? 0),
      ...placement,
      x: asNonNegativeNumber(candidate.x, fallback.x ?? 0),
      y: asNonNegativeNumber(candidate.y, fallback.y ?? 0),
      w: asNonNegativeNumber(candidate.w, fallback.w ?? 1),
      h: asNonNegativeNumber(candidate.h, fallback.h ?? 1),
      minW: asNonNegativeNumber(candidate.minW, fallback.minW ?? 1),
      minH: asNonNegativeNumber(candidate.minH, fallback.minH ?? 1),
      maxW: asNonNegativeNumber(candidate.maxW, fallback.maxW ?? 12),
      maxH: asNonNegativeNumber(candidate.maxH, fallback.maxH ?? 100),
      static: asBoolean(candidate.static, fallback.static ?? false),
    });
  }

  if (normalized.length === 0) {
    return getDefaultDashboardLayoutItems(options?.defaultLayoutVariant ?? "sync");
  }

  for (const cardType of DASHBOARD_CARD_TYPES) {
    if (dedupedCardTypes.has(cardType)) continue;
    const fallback = dashboardCardRegistry[cardType].defaultItem;
    normalized.push({ ...fallback, visible: false });
  }

  return coalesceSplitChartRows(enforceMiniStatSlotLimit(normalized));
}

export function sanitizeLayout(
  input: Partial<DashboardLayout> | null | undefined,
  options?: { defaultLayoutVariant?: DashboardDefaultLayoutVariant }
): DashboardLayout {
  const defaultLayout = getDefaultDashboardLayout(options?.defaultLayoutVariant ?? "sync");
  const normalizedName = typeof input?.name === "string" ? input.name.trim() : "";

  return {
    id: typeof input?.id === "string" && input.id.trim() ? input.id : defaultLayout.id,
    name: normalizedName || defaultLayout.name,
    isDefault: asBoolean(input?.isDefault, defaultLayout.isDefault),
    isActive: asBoolean(input?.isActive, defaultLayout.isActive),
    layoutVersion:
      typeof input?.layoutVersion === "number" && Number.isFinite(input.layoutVersion)
        ? input.layoutVersion
        : DASHBOARD_LAYOUT_VERSION,
    items: normalizeLayoutItems(input?.items, options),
    createdAt:
      typeof input?.createdAt === "string" && input.createdAt
        ? input.createdAt
        : defaultLayout.createdAt,
    updatedAt:
      typeof input?.updatedAt === "string" && input.updatedAt
        ? input.updatedAt
        : new Date().toISOString(),
  };
}
